import { FC, useMemo } from 'react';

import dayjs from 'dayjs';
import { ChevronDown } from 'lucide-react';
import useSWR from 'swr';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';

import { ticketService } from 'v2/api/ticketService';
import { TicketType, UserRewardType } from 'v2/constants';
import { FormReward } from 'v2/types';

const rewardTypeToTicketTypeMap: Partial<Record<UserRewardType, TicketType>> = {
  [UserRewardType.GOLDEN_TICKET]: TicketType.GOLDEN,
  [UserRewardType.CONTEST_TICKET]: TicketType.CONTEST,
};

interface DailyRewardAvailableTicketsProps {
  rewards: FormReward[];
}

export const DailyRewardAvailableTickets: FC<DailyRewardAvailableTicketsProps> = ({ rewards }) => {
  const rewardTypes = useMemo(
    () => rewards.map((reward) => reward.type).filter((type, index, array) => array.indexOf(type) === index),
    [rewards]
  );

  const hasTicketRewards = rewardTypes.some((rewardType) => rewardType in rewardTypeToTicketTypeMap);
  const { data: tickets = [] } = useSWR(hasTicketRewards ? 'getTickets' : null, ticketService.getTickets);

  const groupedTickets = useMemo(() => {
    return rewardTypes.reduce<Record<string, typeof tickets>>((groups, rewardType) => {
      const ticketType = rewardTypeToTicketTypeMap[rewardType];
      if (!ticketType) return groups;

      const filteredTickets = tickets.filter((ticket) => ticket.type === ticketType);
      if (filteredTickets.length > 0) {
        groups[rewardType] = filteredTickets;
      }

      return groups;
    }, {});
  }, [rewardTypes, tickets]);

  if (!hasTicketRewards || Object.keys(groupedTickets).length === 0) {
    return null;
  }

  return (
    <>
      {Object.entries(groupedTickets).map(([rewardType, ticketsList]) => (
        <Accordion key={rewardType}>
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Typography>Available Tickets</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {ticketsList.map(({ id, date, title, description, type }) => (
                <li key={id}>
                  {id}: {dayjs(date).format('YYYY-MM-DD')} {type === TicketType.CONTEST ? title : description}
                </li>
              ))}
            </ul>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};
